import { useState, useEffect } from 'react'
import { X, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  fetchVisiblePatchNotes,
  fetchSeenPatchNotes,
  markPatchNotesSeen,
} from '@/lib/queries/admin'
import { useSession } from '@/context/SessionContext'
import { useProfile } from '@/context/ProfileContext'
import { CATEGORY_CONFIG, CATEGORY_ORDER } from '@/data/patchNoteCategories'
import Modal from './Modal'

export default function UpdateModal() {
  const { user } = useSession()
  const { profile } = useProfile()
  const [unseenNotes, setUnseenNotes] = useState([])
  const [show, setShow] = useState(false)
  const isAdmin = !!profile?.isAdmin
  const userId = user?.id
  const userCreatedAt = user?.created_at

  useEffect(() => {
    if (!user || !profile) return
    async function fetchUnseenNotes() {
      const notes = await fetchVisiblePatchNotes()
      if (!notes?.length) return

      // Non-admins only see live notes
      const filtered = isAdmin ? notes : notes.filter(n => n.status === 'live')
      if (!filtered.length) return

      const seenList = await fetchSeenPatchNotes(userId)
      const seenIds = new Set(seenList)

      const unseen = filtered.filter(note => {
        if (seenIds.has(note.id)) return false
        // Admins see all unseen notes (including admin_only previews)
        if (isAdmin) return true
        // Regular users: only notes released after their account was created
        if (!note.released_at) return false
        return new Date(note.released_at) > new Date(userCreatedAt)
      })

      if (unseen.length > 0) {
        setUnseenNotes(unseen)
        setShow(true)
      }
    }
    fetchUnseenNotes()
  }, [user, profile, isAdmin, userId, userCreatedAt])

  async function handleDismiss() {
    const rows = unseenNotes.map(note => ({
      user_id: user.id,
      patch_note_id: note.id,
    }))
    await markPatchNotesSeen(rows)
    setShow(false)
  }

  if (!show || !unseenNotes.length) return null

  const title = unseenNotes.length > 1 ? `${unseenNotes.length} New Updates` : unseenNotes[0].title
  const titleId = 'update-modal-title'

  return (
    <Modal
      open
      onClose={handleDismiss}
      titleId={titleId}
      overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      className="bg-[var(--bg-elevated)] border border-[var(--glass-border)] rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 pb-4 border-b border-[var(--glass-border)]">
        <div>
          <p className="text-[var(--gold)] text-xs font-semibold uppercase tracking-widest mb-1">
            What's New
          </p>
          <h2 id={titleId} className="font-heading text-xl font-bold text-[var(--text-primary)]">
            {title}
          </h2>
        </div>
        <button
          onClick={handleDismiss}
          className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors p-1"
          aria-label="Close"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="overflow-y-auto flex-1 p-6 space-y-8">
        {unseenNotes.map(note => (
          <div key={note.id}>
            {/* Version + title row */}
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <span className="bg-[var(--gold)]/20 text-[var(--gold)] text-xs font-bold px-3 py-1 rounded-full">
                v{note.version}
              </span>
              <span className="text-[var(--text-primary)] font-semibold">{note.title}</span>
              {note.status === 'admin_only' && (
                <span className="bg-orange-500/20 text-orange-400 text-xs font-medium px-2 py-0.5 rounded-full">
                  Admin Preview
                </span>
              )}
            </div>

            {/* Categories */}
            {CATEGORY_ORDER.map(cat => {
              const cfg = CATEGORY_CONFIG[cat]
              const items = note.patch_note_items
                .filter(i => i.category === cat)
                .sort((a, b) => a.sort_order - b.sort_order)
              if (!items.length) return null
              const Icon = cfg.icon
              return (
                <div key={cat} className="mb-4">
                  <div className={`flex items-center gap-2 mb-2 ${cfg.color}`}>
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      {cfg.label}
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    {items.map(item => (
                      <li
                        key={item.id}
                        className="flex items-start gap-2 text-sm text-[var(--text-muted)]"
                      >
                        <span className="text-[var(--gold)] mt-1.5 text-xs leading-none">◆</span>
                        {item.description}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-6 pt-4 border-t border-[var(--glass-border)] flex items-center justify-between gap-4">
        <Link
          to="/changelog"
          onClick={handleDismiss}
          className="text-sm text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors flex items-center gap-1"
        >
          Full changelog
          <ExternalLink className="w-3 h-3" aria-hidden="true" />
        </Link>
        <button
          onClick={handleDismiss}
          className="bg-[var(--gold)] text-[var(--text-on-gold)] font-bold px-6 py-2.5 rounded-lg hover:opacity-90 transition-all text-sm"
        >
          Got it
        </button>
      </div>
    </Modal>
  )
}
